import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import DeleteModal from '@/components/Modals/DeleteModal';
import { useDeleteSecret, useGetSecrets } from '@/hooks/external-keys/external-keys';
import ArrowDown from '@/images/ArrowDown.svg';
import Plus from '@/images/plus.svg';
import Remove from '@/images/Remove.svg';
import { useFlag } from '@/services/featureFlags';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { ExternalKeysProviders, GetExternalKeysResponseDto, LLM_OPTIONS, SecretConfig } from '@d-id/types';
import styled from '@emotion/styled';
import { ClickAwayListener, List, ListItem, ListItemButton, Tooltip, TooltipProps } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { AddCustomLLMModal } from './AddCustomLLMModal';

const slotProps: TooltipProps['slotProps'] = {
    popper: {
        disablePortal: true,
        sx: {
            width: '100%',
            zIndex: 1600,
        },
        modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
    },
    tooltip: {
        sx: {
            borderRadius: '8px',
            border: `1px solid ${theme.colors.secondary[200]}`,
            background: 'white',
            minWidth: '100% !important',
            margin: '0 !important',
            padding: 0,
            boxShadow:
                '5.3px 13.3px 18.8px -1.2px rgba(187, 187, 187, 0.29), 2.4px 6.1px 8.6px -0.8px rgba(187, 187, 187, 0.23), 1.1px 2.8px 3.9px -0.4px rgba(187, 187, 187, 0.17), 0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.12)',
        },
    },
};

const TooltipContainer = styled.div`
    width: 100%;
    position: relative;
`;

const TriggerContainer = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.secondary[200]};
    border-radius: 8px;

    width: 100%;
`;

const StyledIconButton = styled(IconButton)`
    display: flex;
    justify-content: start;
    padding: 8px 12px;
    width: 100%;
    border-radius: 8px;

    &:focus {
        outline: none;
    }

    &:disabled {
        opacity: 0.5;
    }
    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const MaskedText = styled.p`
    color: ${({ theme }) => theme.colors.black[700]};
    font-weight: 600;
    font-size: 16px;
    line-height: 32px;

    flex: 1;
`;

const ArrowIcon = styled(MaskedIcon)`
    margin-left: 12px;
    color: ${theme.colors.black[700]};
` as typeof MaskedIcon;

const LLM_TITLE_FONT_SIZE = '12px';
const DEFAULT_LLM_MODEL = LLM_OPTIONS.GPT_4O_MINI;
const LLM_OPTIONS_MAP: Record<LLM_OPTIONS, [string, string]> = {
    [LLM_OPTIONS.GPT_35_TURBO]: ['GPT-3.5', 'Reliable legacy model. Good for basic flows'],
    [LLM_OPTIONS.GPT_4O_GLOBAL]: ['GPT-4o', 'Versatile and responsive. Strong choice for general-purpose Agents'],
    [LLM_OPTIONS.GPT_4O_MINI]: ['GPT-4o Mini', 'Lightweight and fast. Best for focused conversations with minimal logic'],
    [LLM_OPTIONS.GPT_41]: ['GPT-4.1', 'Highest quality. Best for complex reasoning and long context'],
    [LLM_OPTIONS.GPT_41_MINI]: ['GPT-4.1 Mini', 'Best balance of quality and speed. Ideal for most Agents'],
    [LLM_OPTIONS.GPT_41_NANO]: ['GPT-4.1 Nano', 'Fastest model. Great for simple, high-speed interactions'],
    [LLM_OPTIONS.EXTERNAL_OPENAI]: ['Custom LLM (OpenAI)', ''],
};

const StyledList = styled(List)`
    width: 100%;
    padding-block: 0;
`;

const OptionContainer = styled.div<{ isPreview: boolean }>`
    display: flex;
    flex-direction: ${({ isPreview }) => (!isPreview ? 'row' : 'column')};
    justify-content: space-between;
    align-items: ${({ isPreview }) => (!isPreview ? 'center' : 'start')};
    width: 100%;
`;

const LLMListItemContainer = styled(ListItem)`
    padding: 8px 12px 8px 12px;
    height: 72px;
`;

const ButtonItemContainer = styled(ListItemButton)`
    padding: 8px 12px 8px 12px;
    height: 56px;
    border-radius: 8px;
`;

const Title = styled.p`
    color: ${theme.colors.secondary.main};
    font-style: normal;
    line-height: 20px;
    font-family: Manrope;
    font-weight: 500;
    letter-spacing: 0.15px;
`;

const SubTitle = styled.p`
    color: ${theme.colors.black[500]};
    text-align: start;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.15px;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

interface LLMOptionProps {
    onSelect?: (llmState: LLM_OPTIONS) => void;
    model: LLM_OPTIONS;
    isPreview: boolean;
}

interface LLMListProp {
    onSelect?: (llmState: LLM_OPTIONS | string) => void;
    secrets?: GetExternalKeysResponseDto[];
    onSecretSelect?: (secret: GetExternalKeysResponseDto) => void;
    onSecretDelete?: (secret: GetExternalKeysResponseDto) => void;
    isDeleting?: boolean;
}

export const LLMOption = ({ model, isPreview, onSelect }: LLMOptionProps) => {
    const isDefault = model === DEFAULT_LLM_MODEL;
    const isCustomLLM = model === LLM_OPTIONS.EXTERNAL_OPENAI;

    return (
        <OptionContainer onClick={() => onSelect?.(model)} isPreview={isPreview}>
            <div>
                <TitleContainer>
                    {isCustomLLM && !isPreview && <MaskedIcon src={Plus} size="14px" color={theme.colors.secondary.main} />}
                    <Title style={{ fontSize: LLM_TITLE_FONT_SIZE }}>{LLM_OPTIONS_MAP[model][0]}</Title>
                </TitleContainer>
                {!isPreview && <SubTitle>{LLM_OPTIONS_MAP[model][1]}</SubTitle>}
            </div>
            {!isPreview && isDefault && <Title style={{ fontSize: LLM_TITLE_FONT_SIZE, marginLeft: '96px' }}> Default </Title>}
        </OptionContainer>
    );
};

export const LLMListItem = ({ model, onSelect, isPreview }: LLMOptionProps) => (
    <LLMListItemContainer disablePadding={isPreview} onClick={() => !isPreview && onSelect?.(model)}>
        <ButtonItemContainer>
            <LLMOption model={model} isPreview={isPreview} />
        </ButtonItemContainer>
    </LLMListItemContainer>
);

interface SecretListItemProps {
    secret: GetExternalKeysResponseDto;
    onSelect?: (secret: GetExternalKeysResponseDto) => void;
    onDelete?: (secret: GetExternalKeysResponseDto) => void;
    isDeleting?: boolean;
}

const SecretListItem = ({ secret, onSelect, onDelete, isDeleting }: SecretListItemProps) => {
    const config = JSON.parse(secret.config || '{}') as SecretConfig;

    const displayName = `${config.model} (Custom)`;

    const handleItemClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('[data-delete-button]')) {
            return;
        }
        onSelect?.(secret);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete?.(secret);
    };

    return (
        <LLMListItemContainer disablePadding>
            <ButtonItemContainer onClick={handleItemClick}>
                <OptionContainer isPreview={false}>
                    <div>
                        <Title style={{ fontSize: LLM_TITLE_FONT_SIZE }}>{displayName}</Title>
                        <SubTitle>Custom LLM</SubTitle>
                    </div>
                    <IconButton
                        data-delete-button
                        onClick={handleDeleteClick}
                        disabled={isDeleting}
                        ariaLabel="Delete custom LLM"
                        disableRipple>
                        <MaskedIcon src={Remove} size="20px" color={theme.colors.black[700]} />
                    </IconButton>
                </OptionContainer>
            </ButtonItemContainer>
        </LLMListItemContainer>
    );
};

export const LLMList = ({ onSelect, secrets, onSecretSelect, onSecretDelete, isDeleting }: LLMListProp) => {
    const customOpenAILLMEnabled = useFlag('custom-openai-llm-enabled', false);

    return (
        <div>
            <StyledList id="llm-select">
                {Object.entries(LLM_OPTIONS_MAP)
                    .filter(([key]) => key !== LLM_OPTIONS.GPT_35_TURBO && key !== LLM_OPTIONS.EXTERNAL_OPENAI)
                    .map(([key]) => (
                        <React.Fragment key={key}>
                            <LLMListItem model={key as LLM_OPTIONS} onSelect={onSelect} isPreview={false} />
                        </React.Fragment>
                    ))}
                {secrets && secrets.length > 0 && (
                    <>
                        {secrets.map(secret => (
                            <SecretListItem
                                key={secret.id}
                                secret={secret}
                                onSelect={onSecretSelect}
                                onDelete={onSecretDelete}
                                isDeleting={isDeleting}
                            />
                        ))}
                    </>
                )}
                {customOpenAILLMEnabled && <LLMListItem model={LLM_OPTIONS.EXTERNAL_OPENAI} onSelect={onSelect} isPreview={false} />}
            </StyledList>
        </div>
    );
};
const LLMPreview = () => {
    const agentEditorStore = useAgentEditorStore();
    const { llm } = agentEditorStore;
    if (llm.provider === LLM_OPTIONS.EXTERNAL_OPENAI) {
        return (
            <OptionContainer isPreview={true}>
                <div>
                    <Title style={{ fontSize: LLM_TITLE_FONT_SIZE }}>{`${llm.model} (Custom)`}</Title>
                </div>
            </OptionContainer>
        );
    }

    return <LLMOption model={llm.model || (LLM_OPTIONS.GPT_4O_MINI as LLM_OPTIONS)} isPreview={true} />;
};

export const LLMSelect = observer(() => {
    const agentEditorStore = useAgentEditorStore();
    const [open, setOpen] = useState(false);
    const [isCustomLLMModalOpen, setIsCustomLLMModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [secretToDelete, setSecretToDelete] = useState<GetExternalKeysResponseDto | null>(null);

    const [pendingSecretId, setPendingSecretId] = useState<string | null>(null);
    const onMenuClick = useCallback(() => setOpen(open => !open), []);

    const { data: secrets = [] } = useGetSecrets(ExternalKeysProviders.OpenAI);
    const { mutateAsync: deleteSecret, isLoading: isDeleting } = useDeleteSecret('Deleted custom llm', ExternalKeysProviders.OpenAI);

    useEffect(() => {
        if (pendingSecretId && secrets.length > 0) {
            const matchingSecret = secrets.find(secret => secret.id === pendingSecretId);

            if (matchingSecret) {
                const config = JSON.parse(matchingSecret.config || '{}') as SecretConfig;

                agentEditorStore.setLLM({
                    provider: LLM_OPTIONS.EXTERNAL_OPENAI as const,
                    secretId: matchingSecret.id,
                    endpoint: config.endpoint,
                    model: config.model,
                    isStreaming: config.isStreaming,
                    maxMessages: config.maxMessages,
                });

                setPendingSecretId(null);
            }
        }
    }, [secrets, pendingSecretId, agentEditorStore]);

    const handleSecretDeleteClick = useCallback((secret: GetExternalKeysResponseDto) => {
        setSecretToDelete(secret);
        setDeleteModalOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!secretToDelete) return;

        const currentLLM = agentEditorStore.llm;
        const isCurrentlySelected =
            currentLLM.provider === LLM_OPTIONS.EXTERNAL_OPENAI && 'secretId' in currentLLM && currentLLM.secretId === secretToDelete.id;

        try {
            await deleteSecret(secretToDelete.id);
            if (isCurrentlySelected) {
                agentEditorStore.setLLM({
                    provider: 'openai' as const,
                    model: DEFAULT_LLM_MODEL,
                });
            }
            setDeleteModalOpen(false);
            setSecretToDelete(null);
        } catch (error) {
            // Error handling is done in the hook
        }
    }, [secretToDelete, deleteSecret, agentEditorStore]);

    const handleModelChange = (value: LLM_OPTIONS | string | null) => {
        if (value === LLM_OPTIONS.EXTERNAL_OPENAI) {
            setOpen(false);
            setIsCustomLLMModalOpen(true);
            return;
        }

        agentEditorStore.setLLM({
            provider: 'openai' as const,
            model: ((value as LLM_OPTIONS) ?? LLM_OPTIONS.GPT_4O_MINI).toLocaleLowerCase() as LLM_OPTIONS,
        });

        setOpen(false);
    };

    const handleSecretSelect = (secret: GetExternalKeysResponseDto) => {
        const config = JSON.parse(secret.config || '{}') as SecretConfig;

        agentEditorStore.setLLM({
            provider: 'external-openai' as const,
            secretId: secret.id,
            endpoint: config.endpoint,
            model: config.model,
            isStreaming: config.isStreaming,
            maxMessages: config.maxMessages,
        });

        setOpen(false);
    };

    const handleCustomLLMSave = (secretId: string) => {
        setPendingSecretId(secretId);
        setIsCustomLLMModalOpen(false);
    };

    return (
        <>
            <ClickAwayListener onClickAway={() => setOpen(false)}>
                <TooltipContainer>
                    <Tooltip
                        slotProps={slotProps}
                        onClose={() => setOpen(false)}
                        open={open}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={
                            <LLMList
                                onSelect={handleModelChange}
                                secrets={secrets}
                                onSecretSelect={handleSecretSelect}
                                onSecretDelete={handleSecretDeleteClick}
                                isDeleting={isDeleting}
                            />
                        }>
                        <TriggerContainer>
                            <StyledIconButton aria-label="open button" onClick={onMenuClick} ariaLabel="Canvas size" disableRipple>
                                <MaskedText>
                                    <LLMPreview />
                                </MaskedText>
                                <ArrowIcon rotated={open} src={ArrowDown} size="2.4rem" />
                            </StyledIconButton>
                        </TriggerContainer>
                    </Tooltip>
                </TooltipContainer>
            </ClickAwayListener>

            {isCustomLLMModalOpen && <AddCustomLLMModal onClose={() => setIsCustomLLMModalOpen(false)} onSave={handleCustomLLMSave} />}

            {secretToDelete && (
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setSecretToDelete(null);
                    }}
                    onDelete={handleConfirmDelete}
                    isLoading={isDeleting}
                    text={{
                        title: 'Confirm delete',
                        content: `Are you sure you want to delete this custom LLM model "${(JSON.parse(secretToDelete.config || '{}') as SecretConfig).model}"? This action cannot be undone.`,
                        cta: 'Delete',
                    }}
                    mode="square"
                    itemType="custom-llm"
                />
            )}
        </>
    );
});
